// sensei-app/client/ui/StudentAssessmentView.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class StudentAssessmentView extends ComponentBase {

    static styles = `
    .assessment-view-container {
        display: flex;
        flex-direction: column;
        height: 100vh;
        width: 100vw;
        overflow: hidden;
        background-color: var(--bs-body-bg);
        position: relative; /* For overlay positioning */
    }
    .assessment-loader-overlay {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-color: rgba(var(--bs-body-bg-rgb), 0.85);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 100;
        backdrop-filter: blur(4px);
    }
    
    /* --- REVISED: Grading Animation --- */
    .grading-animation-container {
        text-align: center;
        font-size: 1.5rem;
        font-weight: 500;
        color: var(--bs-emphasis-color);
        height: 50px; /* Give it a fixed height to prevent layout shifts */
        position: relative;
        width: 100%;
    }
    .grading-text {
        opacity: 0;
        transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
        position: absolute;
        width: 100%;
        left: 0;
        transform: translateY(20px); /* Start off-screen */
    }
    .grading-text.active {
        opacity: 1;
        transform: translateY(0); /* Animate into view */
    }
    /* --- END REVISED --- */

    .assessment-header { padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--bs-border-color); display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; }
    .assessment-title { font-weight: 500; }
    .assessment-timer, .assessment-progress-text { font-family: monospace; }
    .assessment-body { flex-grow: 1; overflow-y: auto; padding: 2rem; }
    .question-container { max-width: 800px; margin: 0 auto; }
    .question-text { font-size: 1.25rem; margin-bottom: 2rem; }
    .answer-options .form-check { margin-bottom: 1rem; font-size: 1.1rem; }
    .assessment-footer { padding: 0.75rem 1.5rem; border-top: 1px solid var(--bs-border-color); display: flex; justify-content: space-between; flex-shrink: 0; }
    .results-summary { text-align: center; }
    .results-summary h2 { font-size: 3rem; font-weight: bold; }
    .results-summary .badge { font-size: 1.2rem; }
    .result-question-item {
        padding: 1rem; margin-bottom: 1rem; border-radius: var(--bs-border-radius);
        background-color: var(--bs-tertiary-bg);
        opacity: 0; transform: translateY(20px); transition: opacity 0.4s ease-out, transform 0.4s ease-out;
    }
    .result-question-item.visible { opacity: 1; transform: translateY(0); }
    .result-question-item.correct { border-left: 5px solid var(--bs-success); }
    .result-question-item.incorrect { border-left: 5px solid var(--bs-danger); }
    .result-question-item.partial { border-left: 5px solid var(--bs-warning); }
`;

    constructor(config) {
        super(config);
        this.scheduleId = null;
        this.session = null;
        this.timerInterval = null;
        this.inputArea = null; // For drawing/text input
        this.isLoading = false;

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.container = document.createElement('div');
        this.container.className = 'assessment-view-container';
        this.inputArea = await this.createManagedComponent('ClassroomInputArea', null, { instanceId: `${this.config.instanceId}-input` });
    }

    async onShow() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        const { scheduleId, sessionId, review } = queryParams;

        if (review === 'true' && sessionId) {
            // --- REVIEW MODE ---
            this.log.info(`Entering review mode for assessment session: ${sessionId}`);
            this.scheduleId = null; // Not starting a new session from a schedule
            await this.loadSpecificResult(sessionId);
        } else if (scheduleId) {
            // --- ACTIVE/NEW ASSESSMENT MODE ---
            // If the scheduleId changes, start a new session.
            if (this.scheduleId !== scheduleId) {
                this.scheduleId = scheduleId;
                await this.startOrResume();
            }
        } else {
            this.showError("No assessment schedule or session specified.");
        }
    }


    async loadSpecificResult(sessionId) {
        try {
            this.session = await this.appClient.rpcClient.call('StudentAssessmentRpc.getSessionState', { sessionId });
            if (this.session.status !== 'completed') {
                throw new Error("This assessment has not been completed yet.");
            }
            this.render(); // This will call renderResultsView
        } catch (error) {
            this.showError(`Could not load assessment results: ${error.message}`);
        }
    }


    async startOrResume() {
        this.isLoading = true;
        this.render(); // Show loader immediately
        try {
            this.session = await this.appClient.rpcClient.call('StudentAssessmentRpc.startOrResumeSession', { scheduleId: this.scheduleId });
            this.startTimer();
        } catch (error) {
            this.showError(`Could not start the assessment: ${error.message}`);
        } finally {
            this.isLoading = false;
            this.render(); // Re-render with content or error
        }
    }


    render() {
        this.container.innerHTML = '';
        if (this.isLoading) {
            this.container.innerHTML = `
            <div class="assessment-loader-overlay">
                <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status"></div>
                <h5 class="text-muted">${this.t('Preparing your assessment...')}</h5>
            </div>`;
            return this.container;
        }

        if (!this.session) {
            // This case is now handled by the showError method or the initial loader
            return this.container;
        }

        if (this.session.status === 'completed' || this.session.status === 'grading') {
            this.renderResultsView();
        } else {
            this.renderAssessmentView();
        }
        return this.container;
    }



    renderAssessmentView() {
        const question = this.session.questions[this.session.currentQuestionIndex];
        if (!question) {
            // This happens when all questions are answered, before the session ends
            this.renderSubmittingView();
            return;
        }

        const header = this.renderHeader(question);
        const body = document.createElement('div');
        body.className = 'assessment-body';
        body.innerHTML = `<div class="question-container">
            <p class="question-text">${question.questionText}</p>
            <div class="answer-options"></div>
        </div>`;

        const optionsContainer = body.querySelector('.answer-options');
        this.renderAnswerInput(question, optionsContainer);

        const footer = this.renderFooter();
        this.container.append(header, body, footer);
    }

    renderAnswerInput(question, container) {
        container.innerHTML = ''; // Clear previous
        const answer = this.session.answers.find(a => a.questionId === question.id)?.answer;

        switch (question.questionType) {
            case 'single-choice':
                question.options.forEach(opt => {
                    container.innerHTML += `<div class="form-check">
                        <input class="form-check-input" type="radio" name="answer" id="opt-${opt}" value="${opt}" ${answer === opt ? 'checked' : ''}>
                        <label class="form-check-label" for="opt-${opt}">${opt}</label>
                    </div>`;
                });
                break;
            case 'multi-choice':
                question.options.forEach(opt => {
                    container.innerHTML += `<div class="form-check">
                        <input class="form-check-input" type="checkbox" name="answer" id="opt-${opt}" value="${opt}" ${(answer || []).includes(opt) ? 'checked' : ''}>
                        <label class="form-check-label" for="opt-${opt}">${opt}</label>
                    </div>`;
                });
                break;
            case 'text':
                container.innerHTML = `<textarea class="form-control" rows="5" placeholder="Type your answer here...">${answer || ''}</textarea>`;
                break;
            case 'drawing':
                container.appendChild(this.inputArea.render());
                this.inputArea.clearDrawing();
                if (answer?.image) this.inputArea.setValue({ image: answer.image }); // Assuming a setValue method
                break;
        }
    }

    renderHeader(question) {
        const header = document.createElement('div');
        header.className = 'assessment-header';
        header.innerHTML = `
            <div class="assessment-title">${this.session.questions[0].title || 'Assessment'}</div>
            <div class="assessment-progress-text">${this.session.currentQuestionIndex + 1} / ${this.session.questions.length}</div>
            <div class="assessment-timer">${this.formatTime(this.session.timeRemainingSeconds)}</div>
        `;
        return header;
    }

    renderFooter() {
        const footer = document.createElement('div');
        footer.className = 'assessment-footer';
        const isLastQuestion = this.session.currentQuestionIndex === this.session.questions.length - 1;
        footer.innerHTML = `
            <button class="btn btn-secondary" id="prev-btn" ${this.session.currentQuestionIndex === 0 ? 'disabled' : ''}>Previous</button>
            <button class="btn btn-primary" id="next-btn">${isLastQuestion ? 'Finish' : 'Next'}</button>
        `;
        footer.querySelector('#prev-btn').addEventListener('click', () => this.submitAndGoTo(this.session.currentQuestionIndex - 1));
        footer.querySelector('#next-btn').addEventListener('click', () => this.submitAndGoTo(this.session.currentQuestionIndex + 1));
        return footer;
    }

    async submitAndGoTo(nextIndex) {
        const nextButton = this.container.querySelector('#next-btn');
        const originalButtonHTML = nextButton.innerHTML;
        nextButton.disabled = true;
        nextButton.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>`;

        try {
            const currentQuestion = this.session.questions[this.session.currentQuestionIndex];
            let answerValue;
            switch (currentQuestion.questionType) {
                case 'single-choice':
                    answerValue = this.container.querySelector('input[name="answer"]:checked')?.value;
                    break;
                case 'multi-choice':
                    answerValue = Array.from(this.container.querySelectorAll('input[name="answer"]:checked')).map(el => el.value);
                    break;
                case 'text':
                    answerValue = this.container.querySelector('textarea').value;
                    break;
                case 'drawing':
                    answerValue = this.inputArea.getValue();
                    break;
            }

            if (answerValue !== undefined) {
                await this.appClient.rpcClient.call('StudentAssessmentRpc.submitAnswer', {
                    sessionId: this.session._id,
                    answerData: { questionId: currentQuestion.id, answer: answerValue }
                });
            }

            if (nextIndex >= this.session.questions.length) {
                await this.appClient.rpcClient.call('StudentAssessmentRpc.endSession', { sessionId: this.session._id });
                this.session.status = 'grading';
                this.render();
            } else {
                this.session.currentQuestionIndex = nextIndex;
                const answerIndex = this.session.answers.findIndex(a => a.questionId === currentQuestion.id);
                if (answerIndex > -1) this.session.answers[answerIndex].answer = answerValue;
                else this.session.answers.push({ questionId: currentQuestion.id, answer: answerValue });
                this.render();
            }
        } catch (error) {
            this.appClient.notifier.error("An error occurred while submitting your answer.");
            this.log.error("Submit answer error:", error);
        } finally {
            if (nextButton) { // Check if button still exists
                nextButton.disabled = false;
                nextButton.innerHTML = originalButtonHTML;
            }
        }
    }

    renderSubmittingView() {
        this.container.innerHTML = `
        <div class="d-flex justify-content-center align-items-center h-100">
            <div class="grading-animation-container">
                <div class="grading-text">${this.t('Finalizing your answers...')}</div>
                <div class="grading-text">${this.t('Grading objective questions...')}</div>
                <div class="grading-text">${this.t('AI is analyzing written responses...')}</div>
                <div class="grading-text">${this.t('Calculating final score...')}</div>
            </div>
        </div>
    `;

        // --- NEW: JavaScript Animation Controller ---
        const texts = this.container.querySelectorAll('.grading-text');
        let currentIndex = 0;

        const cycleText = () => {
            // Remove 'active' from all elements
            texts.forEach(text => text.classList.remove('active'));

            // If we've shown all messages, stop. The polling will take over.
            if (currentIndex >= texts.length) {
                return;
            }

            // Add 'active' to the current element
            const currentText = texts[currentIndex];
            currentText.classList.add('active');

            currentIndex++;
        };

        // Start the cycle immediately and then repeat every 3 seconds
        cycleText();
        this.gradingAnimationInterval = setInterval(cycleText, 3000); // Store interval to clear it later
        // --- END NEW ---
    }

    // Drop-in replacement for the renderResultsView method in StudentAssessmentView.js
    renderResultsView() {
        // --- NEW: Stop the animation when results are ready ---
        if (this.gradingAnimationInterval) {
            clearInterval(this.gradingAnimationInterval);
            this.gradingAnimationInterval = null;
        }
        // --- END NEW ---

        // Poll for results
        const pollInterval = setInterval(async () => {
            const updatedSession = await this.appClient.rpcClient.call('StudentAssessmentRpc.getSessionState', { sessionId: this.session._id });
            if (updatedSession.status === 'completed') {
                clearInterval(pollInterval);
                this.session = updatedSession;
                this.renderCompletedResults();
            }
        }, 3000);

        // If the session is already completed when this is first called, render immediately.
        if (this.session.status === 'completed') {
            clearInterval(pollInterval);
            this.renderCompletedResults();
        } else {
            // Otherwise, show the submitting/grading view while polling.
            this.renderSubmittingView();
        }
    }


    renderCompletedResults() {
        const header = document.createElement('div');
        header.className = 'assessment-header';
        header.innerHTML = `<div class="assessment-title">Assessment Results</div>`;

        const body = document.createElement('div');
        body.className = 'assessment-body';
        const scorePercentage = this.session.maxScore > 0 ? Math.round((this.session.finalScore / this.session.maxScore) * 100) : 0;
        const passStatus = scorePercentage >= 50 ? 'Passed' : 'Failed';

        body.innerHTML = `
        <div class="results-summary">
            <p>Final Score</p>
            <h2>${this.session.finalScore} / ${this.session.maxScore} (${scorePercentage}%)</h2>
            <span class="badge ${passStatus === 'Passed' ? 'bg-success' : 'bg-danger'}">${passStatus}</span>
        </div>
        <hr class="my-4">
        <h5 class="mb-3">Question Breakdown</h5>
        <div class="results-breakdown"></div>
    `;

        const breakdownContainer = body.querySelector('.results-breakdown');
        this.session.questions.forEach(q => {
            const answer = this.session.answers.find(a => a.questionId === q.id);
            if (!answer) return;

            const correctness = answer.score === q.marks ? 'correct' : (answer.score > 0 ? 'partial' : 'incorrect');
            const item = document.createElement('div');
            item.className = `result-question-item ${correctness}`;
            item.innerHTML = `
            <h6>${q.questionText} (${answer.score}/${q.marks} Marks)</h6>
            <p><strong>Your Answer:</strong> ${this.formatAnswerForDisplay(answer.answer)}</p>
            ${correctness !== 'correct' ? `<p><strong>Correct Answer/Rubric:</strong> ${this.formatAnswerForDisplay(q.correctAnswer)}</p>` : ''}
            <p class="text-muted small"><em><strong>Feedback:</strong> ${answer.feedback}</em></p>
        `;
            breakdownContainer.appendChild(item);
        });

        const footer = document.createElement('div');
        footer.className = 'assessment-footer';
        footer.innerHTML = `
        <button class="btn btn-primary" id="retake-btn">Retake Assessment</button>
        <button class="btn btn-secondary" id="back-to-schedule-btn">Back to Schedule</button>
    `;
        footer.querySelector('#retake-btn').addEventListener('click', () => {
            this.appClient.layoutEngine.navigate(`/student/assessment?scheduleId=${this.session.scheduleId}`);
        });
        footer.querySelector('#back-to-schedule-btn').addEventListener('click', () => this.appClient.layoutEngine.navigate('/student/schedule'));

        this.container.innerHTML = '';
        this.container.append(header, body, footer);

        // Staggered fade-in for results
        setTimeout(() => {
            const items = this.container.querySelectorAll('.result-question-item');
            items.forEach((item, index) => {
                setTimeout(() => {
                    item.classList.add('visible');
                }, index * 100); // 100ms delay between each item
            });
        }, 100);
    }


    formatAnswerForDisplay(answer) {
        if (typeof answer === 'object' && answer !== null) {
            if (answer.image) return `<img src="${answer.image}" style="max-width: 200px; border: 1px solid #ccc;"> <p>${answer.text || ''}</p>`;
            return JSON.stringify(answer);
        }
        return answer;
    }

    startTimer() {
        if (this.timerInterval) clearInterval(this.timerInterval);
        if (!this.session.timeRemainingSeconds) return;

        this.timerInterval = setInterval(() => {
            this.session.timeRemainingSeconds--;
            const timerEl = this.container.querySelector('.assessment-timer');
            if (timerEl) timerEl.textContent = this.formatTime(this.session.timeRemainingSeconds);
            if (this.session.timeRemainingSeconds <= 0) {
                clearInterval(this.timerInterval);
                this.appClient.notifier.warn("Time's up! Submitting your assessment.");
                this.submitAndGoTo(this.session.questions.length); // Auto-submit
            }
        }, 1000);
    }

    formatTime(seconds) {
        if (seconds === null || seconds < 0) return 'No Time Limit';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }

    showError(message) { this.container.innerHTML = `<div class="alert alert-danger m-3">${message}</div>`; }
    destroy() {
        clearInterval(this.timerInterval);
        // --- NEW: Clear the grading animation interval ---
        if (this.gradingAnimationInterval) {
            clearInterval(this.gradingAnimationInterval);
        }
        // --- END NEW ---
        super.destroy();
    }
}